/**
 * Utility functions for table operations
 */
import React from 'react';
import { ChatHistoryData } from '../types/sidekick';

// Define minimum and maximum column widths (in pixels)
const MIN_COLUMN_WIDTH = 100;
const MAX_COLUMN_WIDTH = 400;

// Column type definitions
export type ColumnType = 'id' | 'text' | 'date' | 'categorical';
export type SortField = 'emailId' | 'conversationSubject' |
                      'conversationCreatedAt' | 'conversationFeedback' |
                      'feedbackReason' | 'curatedResponses';

// Define base widths for different column types
const BASE_WIDTHS: Record<ColumnType, number> = {
  // IDs typically need less space
  id: 180,
  // Dates need consistent space
  date: 180,
  // Text fields that might contain longer content
  text: 200,
  // Fields with short categorical values
  categorical: 150
};

// Character width approximation (in pixels) - this is an approximation for average character width
const CHAR_WIDTH = 8;

// Define column types for calculation
const COLUMN_TYPES: Record<SortField, ColumnType> = {
  emailId: 'id',
  conversationSubject: 'text',
  conversationCreatedAt: 'date',
  conversationFeedback: 'categorical',
  feedbackReason: 'text',
  curatedResponses: 'categorical'
};

/**
 * Calculate optimal column widths based on content
 * 
 * @param data Array of data items to analyze
 * @param totalTableWidth Total available width for the table (optional)
 * @returns Object with calculated column widths
 */
export const calculateColumnWidths = (
  data: ChatHistoryData[],
  totalTableWidth?: number
): Record<SortField, number> => {
  if (!data || data.length === 0) {
    // Return default widths if no data
    return {
      emailId: BASE_WIDTHS.id,
      conversationSubject: BASE_WIDTHS.text,
      conversationCreatedAt: BASE_WIDTHS.date,
      conversationFeedback: BASE_WIDTHS.categorical,
      feedbackReason: BASE_WIDTHS.text,
      curatedResponses: BASE_WIDTHS.categorical
    };
  }

  // Initialize column width calculations
  const columnWidths: Record<string, number> = {};
  const columnKeys = Object.keys(COLUMN_TYPES) as SortField[];

  // Calculate content-based widths
  columnKeys.forEach(key => {
    // Start with base width for the column type
    let width = BASE_WIDTHS[COLUMN_TYPES[key] || 'text'];
    
    // Find the maximum content length
    const maxContentLength = Math.max(
      ...data.map(item => {
        const value = item[key as keyof ChatHistoryData];
        return value ? String(value).length : 0;
      })
    );
    
    // Calculate width based on content (with some padding)
    const contentBasedWidth = maxContentLength * CHAR_WIDTH + 40; // 40px for padding and sort icon
    
    // Use the larger of base width or content-based width
    width = Math.max(width, contentBasedWidth);
    
    // Apply min/max constraints
    width = Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, width));
    
    columnWidths[key] = width;
  });

  // If total width is provided, adjust columns proportionally
  if (totalTableWidth) {
    const totalCalculatedWidth = Object.values(columnWidths).reduce((sum, width) => sum + width, 0);
    
    if (totalCalculatedWidth > totalTableWidth) {
      // Scale down proportionally if calculated width exceeds available width
      const scaleFactor = totalTableWidth / totalCalculatedWidth;
      
      // First pass: apply scale factor
      Object.keys(columnWidths).forEach(key => {
        columnWidths[key] = Math.max(
          MIN_COLUMN_WIDTH,
          Math.floor(columnWidths[key] * scaleFactor)
        );
      });
      
      // Second pass: check if we still exceed the total width due to minimum constraints
      // and adjust further if needed
      let adjustedTotalWidth = Object.values(columnWidths).reduce((sum, width) => sum + width, 0);
      
      if (adjustedTotalWidth > totalTableWidth) {
        // Calculate how much we need to reduce
        const excess = adjustedTotalWidth - totalTableWidth;
        const columnsToAdjust = Object.keys(columnWidths).filter(
          key => columnWidths[key] > MIN_COLUMN_WIDTH
        );
        
        if (columnsToAdjust.length > 0) {
          // Distribute the excess reduction among columns that can be reduced
          const reductionPerColumn = Math.ceil(excess / columnsToAdjust.length);
          
          columnsToAdjust.forEach(key => {
            const newWidth = columnWidths[key] - reductionPerColumn;
            columnWidths[key] = Math.max(MIN_COLUMN_WIDTH, newWidth);
          });
        }
      }
    }
  }

  return columnWidths;
};

/**
 * Get the column display name from the field name
 *
 * @param field The field name to get the display name for
 * @param columnHeaders Record of column headers
 * @returns The display name for the field
 */
export const getColumnDisplayName = (
  field: SortField,
  columnHeaders: Record<string, string>
): string => {
  const key = Object.keys(columnHeaders)
    .find(key => key.toLowerCase().includes(field.toLowerCase()));
  
  return key ? columnHeaders[key] : field;
};